import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import React, { useMemo } from "react";

import { css } from "@emotion/react";

import { ReactComponent as FilterSvg } from "../../../assets/icons/Icon/DKT/Filter black.svg";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { Option } from "fp-ts/es6/Option";
import { pipe } from "fp-ts/lib/function";
import { option } from "fp-ts";
import { NoImageSvg } from "../../../assets";
import { SwarovskiGenericCarouselComponent } from "../../../components/_shared-components/ui-elements/SwarovskiGenericCarouselComponent/SwarovskiGenericCarouselComponent";
import { StateT } from "../product-comparison-page-component";

import cn from "classnames";
import { CSS_MQ } from "../../../styles/breakpoints";
import { ContentfulImage } from "../../../components/_shared-components/image-related/ContentfulImage";
import { FONTS, fontSize } from "../../../styles/fonts";
import { productNameSort } from "@swo/shared";

export const PCPSelectionCarousel: ReactTFC<{
  visible: boolean;
  selectedCount: number;
  products: Array<{
    key: string;
    label: string;
    image: Option<{ src: string; alt: string | undefined; sys: { id: string } }>;
    alreadySelected: boolean;
    productRef: StateT["selectedProducts"][0];
  }>;
  filters: Array<{ label: string; isSelected: boolean; onClick: () => void }>;
  resetFilters: () => void;
  onSelected: (product: StateT["selectedProducts"][0]) => void;
}> = props => {
  const carouselElementsMemo = useMemo(() => {
    return [...props.products]
      .sort((a, b) => productNameSort(a.label, b.label))
      .map(product => {
        return (
          <CarouselElement
            key={product.key}
            label={product.label}
            image={product.image}
            alreadySelected={product.alreadySelected}
            onClick={() => props.onSelected(product.productRef)}
          />
        );
      });
  }, [props.products, props.onSelected]);

  return (
    <div
      className={cn(props.visible ? "visible" : undefined, `selected-${props.selectedCount}`)}
      css={css`
        position: absolute;
        z-index: 1;

        padding: 25px 0 50px;
        background-color: ${CSS_COLORS.GreenWhite};

        left: 0;
        top: calc(100% + 20px);
        width: 100%;

        &.selected-3 {
          ${CSS_MQ.atMostSm} {
            width: 125vw;
          }
        }
      `}
    >
      <ContentContainer>
        <ul
          css={css`
            display: flex;
            align-items: center;

            margin-bottom: 10px;
          `}
        >
          <FilterSvg
            css={css`
              cursor: pointer;
              flex-shrink: 0;
            `}
            onClick={props.resetFilters}
          />

          {props.filters.map(f => (
            <li
              className={f.isSelected ? "selected" : undefined}
              css={css`
                margin-left: 12px;
                ${CSS_MQ.atLeastSm} {
                  margin-left: 20px;
                }

                &.selected {
                  color: ${CSS_COLORS.Green};
                }
              `}
              key={f.label}
            >
              <button onClick={f.onClick} css={[FONTS.SwarovskiFutura300, fontSize.x2]}>
                {f.label}
              </button>
            </li>
          ))}
        </ul>

        <SwarovskiGenericCarouselComponent
          width={"full"}
          headline={undefined}
          scrollSnap={true}
          bottomText={undefined}
          carouselElements={carouselElementsMemo}
          imageWidth={248}
          elementsAlignment={"to-left"}
          placeholderForNonFilledState={true}
        />
      </ContentContainer>
    </div>
  );
};

const CarouselElement: ReactTFC<{
  label: string;
  image: Option<{ src: string; alt: string | undefined; sys: { id: string } }>;
  alreadySelected: boolean;
  onClick: () => void;
}> = props => {
  const wrapperStyles = css`
    background-color: ${CSS_COLORS.White};

    border-radius: 8px;

    width: calc(100% - 10px);
    height: 150px;

    flex-shrink: 0;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;

    padding: 12px;

    margin-top: 10px;
    margin-bottom: 10px;

    &:not(.selected) {
      border: 2px solid transparent;
    }

    &.selected {
      border: 2px solid ${CSS_COLORS.Green};
      box-shadow: 0px 0px 9px ${CSS_COLORS.Green};
    }

    display: grid;
    grid-template-rows: 1fr auto;
    grid-template-columns: 1fr;
    justify-content: center;
    align-items: center;
  `;

  return (
    <button
      css={wrapperStyles}
      onClick={props.onClick}
      className={props.alreadySelected ? "selected" : undefined}
      disabled={props.alreadySelected}
    >
      {pipe(
        props.image,
        option.fold(
          () => (
            <NoImageSvg
              css={css`
                width: 100%;
                height: 50px;

                margin-left: auto;
                margin-right: auto;

                display: flex;
                justify-content: center;
                align-items: center;

                color: ${CSS_COLORS.GrayDark2024};
              `}
              strokeWidth={1}
            />
          ),
          img => (
            <ContentfulImage
              assetId={img.sys.id}
              css={css`
                width: 70%;
                height: 70px;
                object-fit: contain;

                margin-left: auto;
                margin-right: auto;
              `}
              contentfulLink={{
                url: img.src,
                requestedWidth: 300,
              }}
              alt={img.alt}
            />
          ),
        ),
      )}
      <span
        css={css`
          text-align: center;
          min-height: 40px;
        `}
      >
        {props.label}
      </span>
    </button>
  );
};
